import { createContext } from 'react';
import { BadgeDesign } from '../../../shared/user/badgeDesign';
import { parsePrinterUid, PrinterConfig, ReceiptPrinterBase } from './printers/PrinterBase';
import { ReceiptPrinterManager } from './printers/ReceiptPrinter';
import { CashierAction, CashierState } from './reducer/reducer';

export interface CashierSettingsModel extends CashierPrinterSettings {
  enableESCHotkey: boolean;
  enablePayments: boolean;
  enablePrintingStaffBadges: boolean;
  printSeparateStaffBadge: boolean;
  printSeparateVendorBadge: boolean;
  requireIDScan: boolean;
  badgePrintPreferences: BadgeTypePreference[];
  showReprintModal: boolean;
  overrideDesign?: BadgeDesign;
  NFCTagInterface: string;
  checkinProducts?: number[];
}

interface CashierPrinterSettings {
  /** Primary badge printer. */
  primaryPrinter: string;
  primaryPrinterConfig: PrinterConfig;
  /** Secondary badge printer, for printing minor badges or other alternate badges. */
  secondaryPrinter?: string;
  secondaryPrinterConfig?: PrinterConfig;
}

export interface CashierContextProps {
  receiptPrinterManagers: ReceiptPrinterManager[];
  config: CashierSettingsModel;
  state: CashierState;

  readonly dispatch: (action: CashierAction) => void;
  readonly updateCashierSettings: (settings: Partial<CashierSettingsModel>) => void;
}

export function getReceiptPrinterManagers(): ReceiptPrinterManager[] {
  return [new ReceiptPrinterManager()];
}

export enum PrinterPreferenceMode {
  Primary = 'primary',
  Secondary = 'secondary',
}

interface BadgeTypePreference {
  id: string;
  text: string;
  preference: PrinterPreferenceMode;
}

const validBadgeTypes: BadgeTypePreference[] = [
  { id: 'staff', text: 'Staff badges', preference: PrinterPreferenceMode.Primary },
  { id: 'attendee', text: 'Attendee badges', preference: PrinterPreferenceMode.Primary },
  { id: 'vendor', text: 'Vendor badges', preference: PrinterPreferenceMode.Primary },
  { id: 'minor', text: 'Minor badges', preference: PrinterPreferenceMode.Secondary },
];

export const defaultContext: CashierSettingsModel = {
  NFCTagInterface: 'http:localhost:7070',
  badgePrintPreferences: validBadgeTypes,
  enableESCHotkey: true,
  enablePayments: false,
  enablePrintingStaffBadges: true,
  requireIDScan: false,
  primaryPrinter: 'os:os-print',
  primaryPrinterConfig: {},
  printSeparateStaffBadge: false,
  printSeparateVendorBadge: false,
  showReprintModal: true,
};

export const saveCashierSettings = (settings: CashierSettingsModel): void => {
  localStorage.setItem('cashierSettings', JSON.stringify(settings));
};

export function getReceiptPrinter(settings: CashierContextProps): ReceiptPrinterBase | undefined {
  const printerId = parsePrinterUid();
  if (printerId === undefined) {
    // Can also be interpreted as not caring which printer we use. Find the first one.
    return settings.receiptPrinterManagers.flatMap((m) => m.getPrinters()).at(0);
  }

  const manager = settings.receiptPrinterManagers.find((m) => m.type === printerId.type);
  return manager?.usePrinter(printerId.serial);
}

export const loadCashierSettings = (settings: CashierSettingsModel): CashierSettingsModel => {
  const settingsString = localStorage.getItem('cashierSettings');

  if (!settingsString) {
    return settings;
  }

  const loadedSettings = JSON.parse(settingsString) as CashierSettingsModel;

  return {
    ...settings,
    badgePrintPreferences: loadedSettings.badgePrintPreferences,
    enableESCHotkey: loadedSettings.enableESCHotkey,
    enablePayments: loadedSettings.enablePayments,
    enablePrintingStaffBadges: loadedSettings.enablePrintingStaffBadges,
    printSeparateStaffBadge: loadedSettings.printSeparateStaffBadge,
    checkinProducts: loadedSettings.checkinProducts,
    printSeparateVendorBadge: loadedSettings.printSeparateVendorBadge,
    requireIDScan: loadedSettings.requireIDScan,
    primaryPrinter: loadedSettings.primaryPrinter,
    secondaryPrinter: loadedSettings.secondaryPrinter,
    NFCTagInterface: loadedSettings.NFCTagInterface || settings.NFCTagInterface,
  };
};

export const CashierContext = createContext<CashierContextProps>({
  receiptPrinterManagers: [],
  config: defaultContext,
  updateCashierSettings: () => {
    // No-op
  },
  dispatch: () => {
    // No-op
  },
  state: {} as CashierState,
});
